import {
  getInvoiceTransformationLimitMessage,
  INVOICE_TRANSFORMATION_LIMITS,
  InvoiceTransformationAvailableField,
  InvoiceTransformationRequestUi,
} from '@monetize/types/app';
import { FC, useState } from 'react';
import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';
import { IoMdTrash } from 'react-icons/io';
import {
  MdChevronRight,
  MdContentCopy,
  MdExpandMore,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md';
import {
  MBox,
  MCard,
  MCustomIconButton,
  MDivider,
  MFlex,
  MFormField,
  MInput,
  MText,
  MTooltip,
} from '../Monetize';
import { InvoiceTransformationAddButton } from './InvoiceTransformationAddButton';
import { InvoiceTransformationConditionGroup } from './InvoiceTransformationConditionGroup';
import { InvoiceTransformationOverrides } from './InvoiceTransformationOverrides';
import {
  getArrayErrorMessage,
  getNewConditionGroup,
  summarizeRule,
  supportsBillGroupOverrides,
} from './invoiceTransformation.utils';

interface InvoiceTransformationLineRuleItemProps {
  lineRuleIndex: number;
  accountId: string;
  availableFields: InvoiceTransformationAvailableField[];
  formContext: UseFormReturn<InvoiceTransformationRequestUi>;
  isReadOnly: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  /** Set once the rule limit is reached — a duplicate would exceed it. */
  isDuplicateDisabled: boolean;
  onDuplicate: () => void;
}

export const InvoiceTransformationLineRuleItem: FC<
  InvoiceTransformationLineRuleItemProps
> = ({
  lineRuleIndex,
  accountId,
  availableFields,
  formContext,
  isReadOnly,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  isDuplicateDisabled,
  onDuplicate,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    control,
    formState: { errors },
    watch,
  } = formContext;

  const groupsPath =
    `invoiceLineRules.${lineRuleIndex}.conditionGroups` as const;

  const {
    fields: groupFields,
    append,
    remove,
  } = useFieldArray({
    name: groupsPath,
    control,
  });

  const mode = watch('mode');
  const showOverrides = supportsBillGroupOverrides(mode);
  const ruleErrors = errors.invoiceLineRules?.[lineRuleIndex];

  const rule = watch(`invoiceLineRules.${lineRuleIndex}`);
  const summary = rule ? summarizeRule(rule, availableFields) : '';
  // In read-only mode there's no "+ Add Override" button, so an empty
  // overrides section would render as just a dangling header — hide it.
  const hasOverrides = (rule?.billGroupOverrides ?? []).length > 0;
  const showOverridesSection = showOverrides && (!isReadOnly || hasOverrides);

  return (
    <MBox mb={3}>
      <MCard variant="borderless">
        <MFlex align="center" p={2} gap={2}>
          {/*
           * No tooltips on the reorder arrows: moving a rule shifts the card
           * under the cursor, which would leave a stale tooltip open while a
           * new one opens for whatever button now sits beneath the mouse. The
           * up/down chevrons are self-explanatory; aria-labels cover a11y.
           */}
          {!isReadOnly && (
            <MFlex direction="column">
              <MCustomIconButton
                variant="icon"
                btnSize={4}
                containerSize={5}
                icon={MdKeyboardArrowUp}
                onClick={onMoveUp}
                isDisabled={!canMoveUp}
                iconColor="tPurple.base"
                aria-label="Move rule up"
              />
              <MCustomIconButton
                variant="icon"
                btnSize={4}
                containerSize={5}
                icon={MdKeyboardArrowDown}
                onClick={onMoveDown}
                isDisabled={!canMoveDown}
                iconColor="tPurple.base"
                aria-label="Move rule down"
              />
            </MFlex>
          )}
          <MText fontWeight="semibold" color="tGray.darkPurple" minW={5}>
            {lineRuleIndex + 1}.
          </MText>
          <MTooltip label={isExpanded ? 'Collapse' : 'Expand'} placement="top">
            <MCustomIconButton
              variant="icon"
              icon={isExpanded ? MdExpandMore : MdChevronRight}
              onClick={() => setIsExpanded((prev) => !prev)}
              iconColor="tPurple.base"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            />
          </MTooltip>
          <MBox flex="1">
            <MFormField error={ruleErrors?.name} isRequired>
              <Controller
                name={`invoiceLineRules.${lineRuleIndex}.name`}
                control={control}
                render={({ field }) => (
                  <MInput
                    placeholder="Rule name"
                    isDisabled={isReadOnly}
                    {...field}
                  />
                )}
              />
            </MFormField>
          </MBox>
          {!isReadOnly && (
            <>
              <MTooltip
                label={
                  isDuplicateDisabled
                    ? getInvoiceTransformationLimitMessage(
                        INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules,
                      )
                    : 'Duplicate rule'
                }
                placement="top"
                // A disabled button fires no pointer events, so the tooltip
                // needs a wrapper to hover.
                shouldWrapChildren={isDuplicateDisabled}
              >
                <MCustomIconButton
                  variant="icon"
                  icon={MdContentCopy}
                  onClick={onDuplicate}
                  isDisabled={isDuplicateDisabled}
                  iconColor="tPurple.base"
                  aria-label="Duplicate rule"
                />
              </MTooltip>
              <MTooltip label="Remove rule" placement="top">
                <MCustomIconButton
                  variant="icon"
                  icon={IoMdTrash}
                  onClick={onRemove}
                  iconColor="tRed.base"
                  aria-label="Remove rule"
                />
              </MTooltip>
            </>
          )}
        </MFlex>

        {!isExpanded && summary && (
          <MBox px={4} pb={2}>
            <MText fontSize="sm" color="tGray.darkPurple" truncate>
              {summary}
            </MText>
          </MBox>
        )}

        {isExpanded && (
          <MBox px={4} pb={4}>
            {groupFields.map((groupField, groupIndex) => (
              <MBox key={groupField.id}>
                {groupIndex > 0 && (
                  <MFlex justify="center" my={2}>
                    <MText fontWeight="semibold" color="tGray.darkPurple">
                      OR
                    </MText>
                  </MFlex>
                )}
                <InvoiceTransformationConditionGroup
                  lineRuleIndex={lineRuleIndex}
                  groupIndex={groupIndex}
                  availableFields={availableFields}
                  formContext={formContext}
                  isReadOnly={isReadOnly}
                  canRemoveGroup={groupFields.length > 1}
                  onRemoveGroup={() => remove(groupIndex)}
                />
              </MBox>
            ))}
            {!isReadOnly && (
              <MFlex mt={2} mb={3} align="center" gap={3}>
                <InvoiceTransformationAddButton
                  label="+ Add Condition Set"
                  count={groupFields.length}
                  limit={INVOICE_TRANSFORMATION_LIMITS.conditionGroups}
                  error={getArrayErrorMessage(ruleErrors?.conditionGroups)}
                  onClick={() => append(getNewConditionGroup(mode))}
                />
              </MFlex>
            )}

            {showOverridesSection && (
              <>
                <MDivider my={3} />

                <InvoiceTransformationOverrides
                  lineRuleIndex={lineRuleIndex}
                  accountId={accountId}
                  formContext={formContext}
                  isReadOnly={isReadOnly}
                />
              </>
            )}
          </MBox>
        )}
      </MCard>
    </MBox>
  );
};
